import React, { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import { authAPI } from '../services/api';

const ResetPassword = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const token = useMemo(() => searchParams.get('token') || '', [searchParams]);

  const [temporaryPassword, setTemporaryPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [error, setError] = useState('');
  const [info, setInfo] = useState('');
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);

  useEffect(() => {
    if (!token) {
      setError('De resetlink mist een token. Gebruik de link uit je e-mail opnieuw.');
    }
  }, [token]);

  const handleSubmit = async (event) => {
    event.preventDefault();
    setError('');
    setInfo('');

    if (!token) {
      setError('De resetlink is ongeldig of ontbreekt.');
      return;
    }

    if (newPassword !== confirmPassword) {
      setError('De nieuwe wachtwoorden komen niet overeen.');
      return;
    }

    setLoading(true);

    try {
      const result = await authAPI.resetPassword(token, temporaryPassword, newPassword);
      setInfo(result?.message || 'Wachtwoord succesvol gewijzigd. Je kunt nu inloggen.');
      setTemporaryPassword('');
      setNewPassword('');
      setConfirmPassword('');
      window.setTimeout(() => navigate('/login', { replace: true }), 1800);
    } catch (resetError) {
      setError(resetError.message || 'Wachtwoord resetten mislukt.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div style={styles.page}>
      <Navbar />

      <main style={styles.shell}>
        <section style={styles.card}>
          <div style={styles.header}>
            <div style={styles.badge}>NLBA LMS</div>
            <h1 style={styles.title}>Nieuw wachtwoord instellen</h1>
            <p style={styles.subtitle}>
              Open de link uit je e-mail, vul het tijdelijke wachtwoord in en kies daarna een nieuw wachtwoord.
            </p>
          </div>

          {error && <div style={styles.error}>{error}</div>}
          {info && <div style={styles.info}>{info}</div>}

          <form onSubmit={handleSubmit} style={styles.form}>
            <label style={styles.field}>
              <span style={styles.label}>Tijdelijk wachtwoord</span>
              <input
                type="password"
                value={temporaryPassword}
                onChange={(event) => setTemporaryPassword(event.target.value)}
                style={styles.input}
                autoComplete="one-time-code"
                required
              />
            </label>

            <label style={styles.field}>
              <span style={styles.label}>Nieuw wachtwoord</span>
              <div style={styles.passwordWrap}>
                <input
                  type={showPassword ? 'text' : 'password'}
                  value={newPassword}
                  onChange={(event) => setNewPassword(event.target.value)}
                  style={{ ...styles.input, ...styles.passwordInput }}
                  autoComplete="new-password"
                  minLength={6}
                  required
                />
                <button
                  type="button"
                  aria-label={showPassword ? 'Wachtwoord verbergen' : 'Wachtwoord tonen'}
                  title={showPassword ? 'Wachtwoord verbergen' : 'Wachtwoord tonen'}
                  onClick={() => setShowPassword((prev) => !prev)}
                  style={styles.toggleBtn}
                >
                  {showPassword ? 'Verberg' : 'Toon'}
                </button>
              </div>
            </label>

            <label style={styles.field}>
              <span style={styles.label}>Bevestig nieuw wachtwoord</span>
              <input
                type={showPassword ? 'text' : 'password'}
                value={confirmPassword}
                onChange={(event) => setConfirmPassword(event.target.value)}
                style={styles.input}
                autoComplete="new-password"
                minLength={6}
                required
              />
            </label>

            <button type="submit" style={loading ? styles.primaryLoading : styles.primary} disabled={loading || !token}>
              {loading ? 'Bezig met opslaan...' : 'Wachtwoord opslaan'}
            </button>
          </form>

          <div style={styles.footerActions}>
            <Link to="/login" style={styles.linkButton}>
              Terug naar inloggen
            </Link>
          </div>
        </section>
      </main>

      <Footer />
    </div>
  );
};

const styles = {
  page: {
    minHeight: '100vh',
    display: 'flex',
    flexDirection: 'column',
    background: 'linear-gradient(180deg, #f5f7fa 0%, #eef4f8 100%)'
  },
  shell: {
    flex: 1,
    display: 'grid',
    placeItems: 'center',
    padding: '3rem 1.25rem'
  },
  card: {
    width: '100%',
    maxWidth: '520px',
    background: '#ffffff',
    borderRadius: '20px',
    boxShadow: '0 24px 60px rgba(16, 39, 57, 0.12)',
    border: '1px solid #dbe5ee',
    padding: '2rem'
  },
  header: {
    marginBottom: '1.5rem',
    textAlign: 'left'
  },
  badge: {
    display: 'inline-flex',
    alignItems: 'center',
    padding: '0.35rem 0.75rem',
    borderRadius: '999px',
    background: '#eef7f8',
    color: '#0e4d64',
    fontSize: '0.75rem',
    fontWeight: 700,
    letterSpacing: '0.04em',
    marginBottom: '0.85rem'
  },
  title: {
    margin: 0,
    fontSize: '2rem',
    lineHeight: 1.1,
    color: '#102739'
  },
  subtitle: {
    margin: '0.75rem 0 0',
    color: '#516170',
    lineHeight: 1.6
  },
  error: {
    background: '#fee2e2',
    color: '#b91c1c',
    borderRadius: '12px',
    padding: '0.9rem 1rem',
    marginBottom: '1rem'
  },
  info: {
    background: '#ecfdf5',
    color: '#047857',
    borderRadius: '12px',
    padding: '0.9rem 1rem',
    marginBottom: '1rem'
  },
  form: {
    display: 'flex',
    flexDirection: 'column',
    gap: '1rem'
  },
  field: {
    display: 'flex',
    flexDirection: 'column',
    gap: '0.45rem'
  },
  label: {
    color: '#334155',
    fontSize: '0.92rem',
    fontWeight: 600
  },
  input: {
    width: '100%',
    border: '1px solid #cfd9e3',
    borderRadius: '12px',
    padding: '0.85rem 0.95rem',
    fontSize: '1rem',
    color: '#102739',
    background: '#ffffff'
  },
  passwordWrap: {
    position: 'relative'
  },
  passwordInput: {
    paddingRight: '5rem'
  },
  toggleBtn: {
    position: 'absolute',
    right: '0.5rem',
    top: '50%',
    transform: 'translateY(-50%)',
    border: 'none',
    borderRadius: '999px',
    background: '#eef4f8',
    color: '#0e4d64',
    fontSize: '0.8rem',
    fontWeight: 700,
    padding: '0.45rem 0.75rem',
    cursor: 'pointer'
  },
  primary: {
    border: 'none',
    borderRadius: '12px',
    padding: '0.9rem 1rem',
    background: 'linear-gradient(90deg, #0e4d64, #177e89)',
    color: '#ffffff',
    fontSize: '1rem',
    fontWeight: 700,
    cursor: 'pointer',
    marginTop: '0.25rem'
  },
  primaryLoading: {
    border: 'none',
    borderRadius: '12px',
    padding: '0.9rem 1rem',
    background: '#8fb6c3',
    color: '#ffffff',
    fontSize: '1rem',
    fontWeight: 700,
    cursor: 'not-allowed',
    marginTop: '0.25rem'
  },
  footerActions: {
    marginTop: '1.25rem',
    display: 'flex',
    justifyContent: 'flex-end'
  },
  linkButton: {
    color: '#0e4d64',
    textDecoration: 'none',
    fontWeight: 700
  }
};

export default ResetPassword;
